import { ArrowLeft, Bookmark, Heart, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLibrary } from '@/features/saved/hooks/useLibrary';

type TabType = 'saved' | 'liked';

interface IncomingNavigationState {
  tab?: TabType;
}

function TabButton({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 ${
        active
          ? 'bg-[#2E2A23] text-[#F4F2F2]'
          : 'text-[#888185] hover:bg-[#F4F2F2] hover:text-[#2E2A23]'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium">{label}</span>
      <span
        className={`text-xs px-1.5 py-0.5 rounded-full ${
          active ? 'bg-[#F4F2F2]/20 text-[#F4F2F2]' : 'bg-[#CAC7C7] text-[#888185]'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function LibraryCard({
  item,
  type,
  onRemove,
}: {
  item: {
    content: { id: string; title: string; summary: string; author: string; publishedAt: string };
  };
  type: TabType;
  onRemove: (contentId: string) => void;
}) {
  const navigationState = { origin: 'saved-page' as const, libraryTab: type };
  const formattedDate = new Date(item.content.publishedAt).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'short',
  });

  return (
    <article className="group bg-white rounded-lg border border-[#CAC7C7] hover:border-[#E94E1B]/30 hover:shadow-sm transition-all duration-200">
      <Link to={`/content/${item.content.id}`} state={navigationState} className="block p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-base text-[#2E2A23] line-clamp-2 mb-2 group-hover:text-[#E94E1B] transition-colors">
              {item.content.title}
            </h3>
            <p className="text-sm text-[#888185] line-clamp-2 mb-3">{item.content.summary}</p>
            <div className="flex items-center gap-2 text-xs text-[#888185]">
              <span>{item.content.author}</span>
              <span className="text-[#CAC7C7]">·</span>
              <span>{formattedDate}</span>
            </div>
          </div>

          <div className="flex-shrink-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                type === 'saved' ? 'bg-[#FDEAE3]' : 'bg-[#FEE2E2]'
              }`}
            >
              {type === 'saved' ? (
                <Bookmark className="w-5 h-5 text-[#E94E1B] fill-[#E94E1B]" />
              ) : (
                <Heart className="w-5 h-5 text-[#E94E1B] fill-[#E94E1B]" />
              )}
            </div>
          </div>
        </div>
      </Link>

      <div className="px-4 pb-3 flex justify-end border-t border-[#F4F2F2]">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onRemove(item.content.id);
          }}
          className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-[#888185] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
        >
          <Trash2 className="w-3.5 h-3.5" />
          <span>Remover</span>
        </button>
      </div>
    </article>
  );
}

function EmptyState({ type }: { type: TabType }) {
  const Icon = type === 'saved' ? Bookmark : Heart;
  const title = type === 'saved' ? 'Nenhum item salvo' : 'Nenhum item curtido';
  const description =
    type === 'saved'
      ? 'Salve artigos para ler mais tarde clicando no ícone de marcador.'
      : 'Curta artigos que você achou úteis clicando no ícone de coração.';

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 bg-[#F4F2F2] rounded-full flex items-center justify-center mb-6">
        <Icon className="w-8 h-8 text-[#E94E1B]" />
      </div>
      <h3 className="font-serif text-xl text-[#2E2A23] mb-2">{title}</h3>
      <p className="text-[#888185] max-w-sm">{description}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-lg border border-[#CAC7C7] p-4 animate-pulse">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="h-5 w-3/4 bg-[#CAC7C7] rounded mb-3" />
              <div className="h-4 w-full bg-[#CAC7C7] rounded mb-2" />
              <div className="h-4 w-2/3 bg-[#CAC7C7] rounded mb-3" />
              <div className="flex gap-2">
                <div className="h-3 w-20 bg-[#CAC7C7] rounded" />
                <div className="h-3 w-16 bg-[#CAC7C7] rounded" />
              </div>
            </div>
            <div className="w-10 h-10 bg-[#CAC7C7] rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SavedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>('saved');
  const { savedItems, likedItems, isLoading, unsave, unlike } = useLibrary();

  // Restore tab from navigation state (coming back from content detail)
  useEffect(() => {
    const navState = location.state as IncomingNavigationState | undefined;
    if (navState?.tab) {
      setActiveTab(navState.tab);
    }
  }, [location.state]);

  const items = activeTab === 'saved' ? savedItems : likedItems;
  const handleRemove = activeTab === 'saved' ? unsave : unlike;

  return (
    <div className="min-h-screen w-full bg-[#F4F2F2] text-[#2E2A23] font-sans">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full bg-[#F4F2F2]/90 backdrop-blur-sm border-b border-[#CAC7C7]">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            type="button"
            className="group flex items-center gap-2 text-[#888185] hover:text-[#2E2A23] transition-colors"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            <span className="text-sm font-medium">Voltar</span>
          </button>
          <h1 className="font-serif text-lg font-semibold text-[#2E2A23]">Biblioteca</h1>
          <div className="w-16" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8 p-1 bg-[#F4F2F2] rounded-xl w-fit">
          <TabButton
            active={activeTab === 'saved'}
            onClick={() => setActiveTab('saved')}
            icon={Bookmark}
            label="Salvos"
            count={savedItems.length}
          />
          <TabButton
            active={activeTab === 'liked'}
            onClick={() => setActiveTab('liked')}
            icon={Heart}
            label="Curtidos"
            count={likedItems.length}
          />
        </div>

        {/* Content */}
        {isLoading ? (
          <LoadingSkeleton />
        ) : items.length === 0 ? (
          <EmptyState type={activeTab} />
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <LibraryCard
                key={`${activeTab}-${item.content.id}`}
                item={item}
                type={activeTab}
                onRemove={handleRemove}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
